/**
 * Splits a comma-separated argument list, ignoring commas nested inside glob
 * braces or character classes.
 *
 * Namespace patterns use globset syntax, where `{a,b}` alternation and `[a,b]`
 * character classes both contain commas that are part of a single argument.
 * Splitting those would turn one pattern into several malformed ones.
 */
export function splitArgumentList(value: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";

  for (const char of value) {
    if (char === "{" || char === "[") {
      depth++;
    } else if (char === "}" || char === "]") {
      depth = Math.max(0, depth - 1);
    }

    if (char === "," && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  parts.push(current);

  return parts.map((s) => s.trim()).filter((s) => s.length > 0);
}

/**
 * Converts FormData to JSON object with proper type conversions
 * Handles boolean checkboxes, string arrays, and other types
 */
export function formDataToJson(
  formData: FormData,
  operationArguments?: Record<string, { type: string; required?: boolean }>,
): Record<string, any> {
  const jsonData: Record<string, any> = {};

  // First, get all form data entries
  for (const [key, value] of formData.entries()) {
    jsonData[key] = value;
  }

  // Early return if no operation arguments to process
  if (!operationArguments) {
    return jsonData;
  }

  // Process based on operation arguments
  Object.entries(operationArguments).forEach(([argName, properties]) => {
    // Handle string_array and array types
    if (
      (properties.type === "string_array" || properties.type === "array") &&
      argName in jsonData
    ) {
      const value = jsonData[argName] as string;
      jsonData[argName] = splitArgumentList(value);
    }

    // Handle boolean checkboxes
    if (properties.type === "bool") {
      // Checkboxes are present in FormData only when checked
      // If not present, it means unchecked (false)
      // If present with value "on" or "", it means checked (true)
      jsonData[argName] = argName in jsonData;
    }

    if (properties.type === "object") {
      const value = jsonData[argName];
      if (typeof value === "string") {
        try {
          // Parse JSON object
          jsonData[argName] = JSON.parse(value);
        } catch (e) {
          console.error(`Invalid JSON for ${argName}:`, e);
        }
      }
    }
  });

  return jsonData;
}
